import React from "react";
import PropTypes from "prop-types";
import ButtonComponent from "./ButtonComponent";
import classNames from "classnames";

export default function ButtonContainer({
  id,
  size = "base",
  block = false,
  variant = "primary",
  href,
  label = "",
  startIcon = null,
  endIcon = null,
  disabled = false,
  onClick = null,
  justify = "center",
  active,
  isLoading = false,
  type = "button",
  form,
  children = null,
  stopEventBubbling = false,
  logImpression = false,
  logEventProps = {},
  customContainerClass = "",
  ...props
}) {
  let classes = classNames({
    "w-full flex": block,
    "inline-flex": !block,
    "min-w-96": !!label && (variant === "primary" || variant === "secondary"),
    "bg-light": active && variant === "text",
    "font-semibold":
      !(variant.startsWith("text") || variant.startsWith("outline")) ||
      (active && variant === "text"),
    "font-medium":
      (variant.startsWith("text") && !active) ||
      variant.startsWith("outline") ||
      variant.startsWith("action"),
    "rounded-md sm:rounded-lg": !variant.includes("icon"),
    "rounded-full": variant.includes("icon"),
  });

  const variantClass = classNames({
    "bg-primary text-white hover:bg-primary-dark-100": variant === "primary",
    "bg-secondary text-white hover:bg-opacity-75": variant === "secondary",
    "bg-error text-white hover:bg-error-dark-100": variant === "error",
    "bg-light text-secondary hover:bg-light-200 dark:bg-space-80 dark:text-white dark:hover:bg-space-60":
      variant === "light",
    "bg-transparent text-secondary hover:bg-light": variant === "text",
    "bg-transparent text-secondary dark:text-white hover:underline":
      variant === "text-underline",
    "shadow-primary-inner-1 bg-transparent hover:bg-primary hover:bg-opacity-10 text-primary":
      variant === "outline-primary",
    "shadow-secondary-inner-1 bg-transparent text-secondary hover:bg-light":
      variant === "outline-secondary",
    "text-tertiary hover:underline": variant === "action-tertiary",
    "bg-white hover:bg-space-20": variant === "icon",
    "bg-white border border-space-40 filter hover:drop-shadow-icon-button":
      variant === "outline-icon",
  });

  const justifyClass = classNames({
    "justify-center": justify === "center",
    "justify-start": justify === "start",
    "justify-end": justify === "end",
    "justify-between": justify === "between",
  });

  const normalSizeClass = classNames({
    "px-5 py-2.5": !variant.includes("icon") && variant !== "text-underline",
    "p-3": variant.includes("icon"),
    "px-0 py-2.5": variant === "text-underline",
  });

  const textClass = classNames({
    "text-base leading-6 -tracking-wider": size === "base",
    "text-sm leading-5 -tracking-wider": size === "sm",
    "text-xs leading-4 -tracking-wider": size === "xs",
    "md:text-base md:leading-6 -tracking-wider sm:text-sm sm:leading-5 text-xs leading-4":
      size === "responsive-base",
  });

  const getFormattedStartIcon = (icon) => {
    if (!icon) {
      return null;
    }
    const spacingClass = classNames({
      "h-6 w-6": size === "base",
      "h-4 w-4": size === "sm",
      "h-3 w-3": size === "xs",
    });
    return <div className={spacingClass}>{icon}</div>;
  };

  return (
    <ButtonComponent
      id={id}
      size={size}
      block={block}
      variant={variant}
      href={href}
      label={label}
      startIcon={
        variant.includes("icon") ? getFormattedStartIcon(startIcon) : startIcon
      }
      endIcon={
        variant.includes("icon") ? getFormattedStartIcon(endIcon) : endIcon
      }
      disabled={disabled}
      onClick={onClick}
      justify={justify}
      active={active}
      isLoading={isLoading}
      type={type}
      form={form}
      children={children}
      stopEventBubbling={stopEventBubbling}
      logImpression={logImpression}
      logEventProps={logEventProps}
      classes={`${customContainerClass} ${classes} ${variantClass} ${justifyClass} ${textClass} ${
        variant.startsWith("action") ? "" : normalSizeClass
      }`}
      {...props}
    />
  );
}

ButtonContainer.propTypes = {
  /** This sets the size of the button  */
  size: PropTypes.oneOf(["xs", "sm", "base", "responsive-base"]),

  /** This will make the button cover full width  */
  block: PropTypes.bool,

  /**  buttons may be one of a variety of visual variants such as: */
  variant: PropTypes.oneOf([
    "primary",
    "secondary",
    "light",
    "error",
    "text",
    "text-underline",
    "outline-primary",
    "outline-secondary",
    "action-tertiary",
    "icon",
    "outline-icon",
  ]),

  /** This will render the icon before the label */
  startIcon: PropTypes.element,

  /** This will render the icon at after the label */
  endIcon: PropTypes.element,

  /** The Main Label of the Button */
  label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),

  /** This will be used to show a button in disabled state */
  disabled: PropTypes.bool,

  /** This will be used to show a button in loading state. All the other states except the variant will be ignored. */
  isLoading: PropTypes.bool,

  /** This will set the alignment of the items inside button */
  justify: PropTypes.oneOf(["start", "center", "end", "between"]),

  /** This will be used to add custom function that should be executed when we click on the button */
  onClick: PropTypes.func,

  /** Providing a href will render an <Link />, styled as a button. */
  href: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      to: PropTypes.string,
      state: PropTypes.object,
    }),
  ]),

  /** This will explicitly set the button state as ACTIVE*/
  active: PropTypes.bool,

  /** React.Element as children */
  children: PropTypes.element,

  /** This will allow event bubbling in onClick Event. */
  stopEventBubbling: PropTypes.bool,

  /**This prop tells whether to log this buttons activity or not */
  logImpression: PropTypes.bool,

  /** This object contains all the data that is required to log in activity service */
  logEventProps: function (props, propName) {
    // Skip validation if logImpression is not enabled
    if (!props["logImpression"]) return null;

    const logEventProps = props[propName];
    if (
      !logEventProps ||
      typeof logEventProps !== "object" ||
      !("eventName" in logEventProps) ||
      typeof logEventProps["eventName"] !== "string"
    ) {
      return new Error("eventName of type string is required in logEventProps");
    }
  },
};
